import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import { Input } from "@shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/components/ui/table";
import HeadLayout from "@shared/layouts/HeadLayout";
import useTitle from "@shared/hooks/useTitle";
import { DatePickerWithRange } from "@shared/components/date-picker-with-range";
import { startOfDay, endOfDay, subDays, format } from "date-fns";
import {
  Shield,
  AlertTriangle,
  Info,
  XCircle,
  CheckCircle,
  Search,
  Download,
  RefreshCw,
  Filter,
} from "lucide-react";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import { toast } from "sonner";
import { ADMIN_QUERY_KEYS, LOG_TYPES } from "../constants";
import { adminLogsService } from "../services/logs-service";

export default function SystemLogsPage() {
  const { t } = useTranslation();
  useTitle(t("admin.logs.title"));

  const [filters, setFilters] = useState({
    level: "all",
    search: "",
    from: startOfDay(subDays(new Date(), 7)),
    to: endOfDay(new Date()),
    page: 1,
    limit: 50,
  });

  // Fetch logs
  const { data, isLoading, refetch } = useQuery({
    queryKey: [ADMIN_QUERY_KEYS.LOGS, filters],
    queryFn: () => adminLogsService.getLogs(filters),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Auto-refresh every minute
  });

  const handleDateChange = (range) => {
    if (range?.from && range?.to) {
      setFilters((prev) => ({
        ...prev,
        from: startOfDay(range.from),
        to: endOfDay(range.to),
      }));
    }
  };

  const handleExport = async () => {
    try {
      toast.info(t("admin.logs.exportingLogs"));
      await adminLogsService.exportLogs(filters);
      toast.success(t("admin.logs.logsExported"));
    } catch {
      toast.error(t("admin.logs.exportFailed"));
    }
  };

  const logs = data?.data?.logs || [];
  const totalLogs = data?.data?.total || 0;

  // Get log level icon and color
  const getLogLevelStyles = (level) => {
    const styles = {
      error: {
        icon: XCircle,
        color: "text-red-500",
        bg: "bg-red-50",
        badge: "destructive",
      },
      warning: {
        icon: AlertTriangle,
        color: "text-yellow-500",
        bg: "bg-yellow-50",
        badge: "warning",
      },
      info: {
        icon: Info,
        color: "text-blue-500",
        bg: "bg-blue-50",
        badge: "default",
      },
      security: {
        icon: Shield,
        color: "text-purple-500",
        bg: "bg-purple-50",
        badge: "secondary",
      },
      audit: {
        icon: CheckCircle,
        color: "text-green-500",
        bg: "bg-green-50",
        badge: "outline",
      },
      debug: {
        icon: Info,
        color: "text-gray-500",
        bg: "bg-gray-50",
        badge: "outline",
      },
    };
    return styles[level] || styles.info;
  };

  return (
    <div className="space-y-6">
      <HeadLayout title={t("admin.logs.title")}>
        <div className="flex gap-2">
          <Button onClick={refetch} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            {t("admin.logs.refresh")}
          </Button>
          <Button onClick={handleExport} variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            {t("admin.logs.export")}
          </Button>
        </div>
      </HeadLayout>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium mb-2 block">
                {t("admin.logs.logLevel")}
              </label>
              <Select
                value={filters.level}
                onValueChange={(value) =>
                  setFilters((prev) => ({ ...prev, level: value, page: 1 }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t("admin.logs.allLevels")}
                  </SelectItem>
                  {LOG_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {t(type.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium mb-2 block">
                {t("admin.logs.dateRange")}
              </label>
              <DatePickerWithRange
                initialDate={{ from: filters.from, to: filters.to }}
                onSelectDate={handleDateChange}
              />
            </div>

            <div className="flex-1 min-w-[300px]">
              <label className="text-sm font-medium mb-2 block">
                {t("admin.logs.search")}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("admin.logs.searchPlaceholder")}
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      search: e.target.value,
                      page: 1,
                    }))
                  }
                  className="pl-10"
                />
              </div>
            </div>

            <Button
              variant="outline"
              onClick={() =>
                setFilters({
                  level: "all",
                  search: "",
                  from: startOfDay(subDays(new Date(), 7)),
                  to: endOfDay(new Date()),
                  page: 1,
                  limit: 50,
                })
              }
            >
              <Filter className="h-4 w-4 mr-2" />
              {t("admin.logs.clearFilters")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t("admin.logs.systemLogs")}</CardTitle>
            <Badge variant="outline">
              {totalLogs} {t("admin.logs.entries")}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <ThreeDotLoader />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {t("admin.logs.noLogsFound")}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-32">
                      {t("admin.logs.level")}
                    </TableHead>
                    <TableHead className="w-48">
                      {t("admin.logs.timestamp")}
                    </TableHead>
                    <TableHead>{t("admin.logs.message")}</TableHead>
                    <TableHead className="w-40">
                      {t("admin.logs.user")}
                    </TableHead>
                    <TableHead className="w-32">
                      {t("admin.logs.action")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log, index) => {
                    const styles = getLogLevelStyles(log.level);
                    const LogIcon = styles.icon;

                    return (
                      <TableRow key={log._id || index}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={`p-1 rounded ${styles.bg}`}>
                              <LogIcon className={`h-4 w-4 ${styles.color}`} />
                            </div>
                            <Badge
                              variant={styles.badge}
                              className="capitalize"
                            >
                              {log.level}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {format(
                            new Date(log.timestamp),
                            "MMM dd, yyyy HH:mm:ss"
                          )}
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{log.message}</p>
                          {log.metadata && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {JSON.stringify(log.metadata).substring(0, 100)}
                              {JSON.stringify(log.metadata).length > 100 &&
                                "..."}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {log.user?.brandName ||
                            log.user?.email ||
                            t("admin.logs.system")}
                        </TableCell>
                        <TableCell>
                          {log.action && (
                            <Badge variant="outline" className="text-xs">
                              {log.action}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalLogs > filters.limit && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                {t("admin.logs.showing", {
                  from: (filters.page - 1) * filters.limit + 1,
                  to: Math.min(filters.page * filters.limit, totalLogs),
                  total: totalLogs,
                })}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page === 1}
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, page: prev.page - 1 }))
                  }
                >
                  {t("admin.logs.previous")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page * filters.limit >= totalLogs}
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, page: prev.page + 1 }))
                  }
                >
                  {t("admin.logs.next")}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
